<script setup lang="ts">
const { data: page } = await useAsyncData('lehrgaenge-coden-mit-ki-einstieg', () => queryCollection('lehrgaenge_coden_mit_ki_einstieg').first())

const title = page.value?.seo?.title || page.value?.title
const description = page.value?.seo?.description || page.value?.description

useSeoMeta({
  titleTemplate: '',
  title,
  ogTitle: title,
  description,
  ogDescription: description
})

const { courses, isLoading: isLoadingCourses } = useLiveCourses('coden-mit-ki-einstieg', 'courses-coden-mit-ki-einstieg')
// Hero-CTA: mit ausgeschriebenem Kurs direkt zur Anmeldung, sonst zum Anmelde-/Interesse-Block unten.
const heroCtaTo = computed(() => courses.value.length ? `/lehrgang/anmeldung?kurs=${courses.value[0]!.id}` : '#anmeldung')
// CTA-Text je nach Kurslage:
// - Kurs mit Datum ausgeschrieben → Anmelde-Aufruf über den Karten, ohne Warteliste-Links.
// - kein Kurs → voller "Interesse"-Block mit Warteliste-Links.
// Während des Ladens kein Text, um Flackern zu vermeiden.
const ctaProps = computed(() => {
  const cta = page.value?.cta
  if (!cta || isLoadingCourses.value) return {}
  if (courses.value.length) {
    return { title: cta.title, description: cta.descriptionCourses ?? cta.description }
  }
  return cta
})

useJsonLd(() => ({
  '@type': 'Course',
  '@id': `${absoluteUrl('/lehrgang/coden-mit-ki-einstieg')}/#course`,
  'name': title,
  description,
  'url': absoluteUrl('/lehrgang/coden-mit-ki-einstieg'),
  'provider': { '@id': ORGANIZATION_ID },
  'hasCourseInstance': courses.value.map(course => ({
    '@type': 'CourseInstance',
    'courseMode': 'Online',
    'startDate': course.startdate,
    'endDate': course.enddate || undefined,
    ...(course.price_chf
      ? { offers: { '@type': 'Offer', 'price': course.price_chf, 'priceCurrency': 'CHF', 'url': absoluteUrl('/lehrgang/coden-mit-ki-einstieg') } }
      : {})
  }))
}), 'course-schema')
</script>

<template>
  <div v-if="page">
    <UPageHero
      :description="page.description"
      align="left"
    >
      <template #title>
        <MDC
          :value="page.title"
          unwrap="p"
        />
      </template>

      <template #headline>
        <UBadge
          v-if="page.badge"
          :label="page.badge.label"
          :color="page.badge.color"
          :icon="page.badge.icon"
          size="lg"
          variant="subtle"
        />
      </template>

      <template #links>
        <UButton
          :to="heroCtaTo"
          label="Jetzt anmelden"
          trailing-icon="i-lucide-arrow-right"
          size="lg"
          class="w-full sm:w-auto justify-center"
        />
      </template>
    </UPageHero>

    <UPageSection
      v-for="(section, index) in page.sections"
      :key="index"
      :title="section.title"
      :description="section.description"
      :orientation="section.orientation"
      :reverse="section.reverse ?? false"
      :features="section.features"
    >
      <NuxtImg
        v-if="section.image?.src"
        :src="section.image.src"
        :alt="section.image.alt || section.title"
        class="w-full rounded-lg shadow-xl"
      />
    </UPageSection>

    <UPageSection>
      <UPageGrid>
        <UPageCard
          v-for="(fact, index) in page.facts"
          :key="index"
          :title="fact.label"
          :icon="fact.icon"
          variant="subtle"
        >
          <template #description>
            <span v-html="fact.value" />
          </template>
        </UPageCard>
      </UPageGrid>
    </UPageSection>

    <USeparator />

    <UPageSection
      :title="page.modules.title"
      :description="page.modules.description"
    >
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <UPageCard
          v-for="(item, index) in page.modules.items"
          :key="index"
          v-bind="item"
          spotlight
        />
      </div>
    </UPageSection>

    <USeparator />

    <UPageCTA
      id="anmeldung"
      v-bind="ctaProps"
      variant="naked"
      class="overflow-hidden"
    >
      <LazyStarsBg />

      <div
        v-if="isLoadingCourses"
        class="flex justify-center py-8"
      >
        <UIcon
          name="i-lucide-loader-circle"
          class="animate-spin text-2xl text-primary"
        />
      </div>

      <div
        v-else-if="courses.length"
        class="grid gap-4 md:grid-cols-2 w-full"
      >
        <CourseCard
          v-for="course in courses"
          :key="course.id"
          :course="course"
          registration-base="/lehrgang/anmeldung"
        />
      </div>

      <WaitinglistForm
        v-else
        coursetype-slug="coden-mit-ki-einstieg"
        coursetype-title="Coden mit KI – Einstieg"
      />
    </UPageCTA>

    <InfoanlassSection />
  </div>
</template>
